import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Card from "components/card";
import CardMenu from "components/card/CardMenu";
import Pagination from "components/common/Pagination";
import AddEmployeeModal from "components/modal/AddEmployeeModal";
import employeeAPI from "services/employeeAPI";
import wfhAPI from "services/wfh";
import { showError, showSuccess } from "utils/toastHelper";
import { FaEye } from "react-icons/fa";
import { MdEdit, MdDelete } from "react-icons/md";
import Swal from "sweetalert2";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import { useAuth } from "contexts/AuthContext";
import { IoDocumentText } from "react-icons/io5";
import maleProfile from "assets/img/avatars/male_profile.png";
import femaleProfile from "assets/img/avatars/female_profile.png";

const columnHelper = createColumnHelper();

const Employee = () => {
  const { isSuperAdmin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sorting, setSorting] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [selectedEmployeeForDocs, setSelectedEmployeeForDocs] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [authTogglingId, setAuthTogglingId] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState("-");
  const [isAdmin, setIsAdmin] = useState(false);
  const [probationTogglingId, setProbationTogglingId] = useState(null);
  const [wfhTogglingId, setWfhTogglingId] = useState(null);
  // ── NEW: probation filter state ──
  const [probationFilter, setProbationFilter] = useState("all"); // "all" | "on" | "off"
  const [authorizedFilter, setAuthorizedFilter] = useState("all"); // "all" | "authorized" | "not_authorized"
  const [wfhFilter, setWfhFilter] = useState("all"); // "all" | "wfh" | "not_wfh"
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    const userData = localStorage.getItem("user");
    const isSuperAdmin = localStorage.getItem("is_super_admin");
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setCurrentUserRole(user.role || "-");
      } catch (e) {
        console.error("Failed to parse user data", e);
      }
    }
    setIsAdmin(isSuperAdmin === "true" || isSuperAdmin === true);
  }, []);

  const fetchEmployees = () => {
    setLoading(true);
    employeeAPI.getAllEmployees(
      (data) => {
        try {
          let employeeArray = [];
          if (Array.isArray(data)) {
            employeeArray = data;
          } else if (data?.results && Array.isArray(data.results)) {
            employeeArray = data.results;
          } else if (data?.data && Array.isArray(data.data)) {
            employeeArray = data.data;
          } else if (data?.employee && Array.isArray(data.employee)) {
            employeeArray = data.employee;
          } else {
            employeeArray = [];
          }

          const transformedData = employeeArray.map((emp) => {
            let roleName = "-";
            if (emp.role) {
              if (typeof emp.role === "object" && emp.role.role_name) {
                roleName = emp.role.role_name || "-";
              } else if (typeof emp.role === "string") {
                roleName = emp.role || "-";
              }
            }

            let departmentName = "-";
            if (emp.department) {
              if (
                (typeof emp.department === "object" &&
                  emp.department.department_name) ||
                "-"
              ) {
                departmentName = emp.department.department_name || "-";
              } else if (typeof emp.department === "string") {
                departmentName = emp.department || "-";
              }
            }

            return {
              id: emp.id,
              user_id: emp.user_id || emp.user?.id || emp.user || null,
              employee_id: emp.employee_id || emp.id || null,
              name: `${emp.user_name || "-"}`.trim(),
              email: emp.email || "-",
              role: roleName || "-",
              department: departmentName || "-",
              status: emp.is_active ? "Active" : "Inactive",
              joinDate: emp.doj_date
                ? new Date(emp.doj_date).toLocaleDateString()
                : "-",
              ...emp,
            };
          });

          setEmployees(transformedData);
          setLoading(false);
        } catch (error) {
          showError("Error loading employee data. Please refresh the page.");
          setLoading(false);
        }
      },
      (error) => {
        if (error?.status === 401) {
          showError("Unauthorized. Please login again.");
          logout();
        } else if (error?.status === 403) {
          showError("You do not have permission to view employees.");
        } else if (error?.status === 404) {
          showError("Employee endpoint not found.");
        } else if (error?.status >= 500) {
          showError("Server error. Please try again later.");
        } else {
          showError(
            error?.message ||
              "Failed to load employees. Please check your connection."
          );
        }
        setEmployees([]);
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const handleEmployeeSubmit = () => {
    setTimeout(() => {
      fetchEmployees();
      setIsModalOpen(false);
      setIsEditMode(false);
      setEditingEmployee(null);
    }, 500);
  };

  const handleProbationToggle = (emp, currentIsProbationary) => {
    const actionText = currentIsProbationary
      ? "end probation"
      : "start probation";
    const confirmText = currentIsProbationary
      ? "Yes, End Probation"
      : "Yes, Start Probation";
    const confirmButtonColor = currentIsProbationary ? "#dc2626" : "#16a34a";

    Swal.fire({
      title: "Confirm Probationary Period Change",
      text: `Are you sure you want to ${actionText} for ${
        emp.user_name || "-"
      }?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: confirmButtonColor,
      cancelButtonColor: "#6b7280",
      confirmButtonText: confirmText,
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        updateEmployeeProbation(emp, !currentIsProbationary);
      }
    });
  };

  const updateEmployeeProbation = (emp, newProbationStatus) => {
    setProbationTogglingId(emp.id);
    const probationData = {
      id: emp.id,
      is_probationary: newProbationStatus,
    };
    employeeAPI.updateEmployeeProbationStatus &&
      employeeAPI.updateEmployeeProbationStatus(
        emp.id,
        probationData,
        (response) => {
          showSuccess(
            `Employee probationary period ${
              newProbationStatus ? "started" : "ended"
            }`
          );
          fetchEmployees();
          setProbationTogglingId(null);
        },
        (error) => {
          showError(error?.message || "Failed to update probationary period");
          setProbationTogglingId(null);
        }
      );
  };

  const handleViewDocuments = (employee) => {
    setSelectedEmployeeForDocs(employee);
    setDocumentModalOpen(true);
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (!location.state?.openProfileEdit || employees.length === 0) {
      return;
    }

    const userIds = [
      localStorage.getItem("user_id"),
      localStorage.getItem("employee_id"),
      localStorage.getItem("emp_id"),
    ]
      .filter((value) => value !== null && value !== undefined && value !== "")
      .map((value) => String(value));

    const currentEmployee = employees.find((employee) => {
      const candidateIds = [
        employee.id,
        employee.user_id,
        employee.employee_id,
        employee.emp_id,
      ]
        .filter(
          (value) => value !== null && value !== undefined && value !== ""
        )
        .map((value) => String(value));

      return candidateIds.some((candidateId) => userIds.includes(candidateId));
    });

    if (currentEmployee) {
      handleEditEmployee(currentEmployee);
    }

    navigate(location.pathname, { replace: true, state: {} });
  }, [employees, location.pathname, location.state, navigate]);

  const handleStatusToggle = (emp, currentIsActive) => {
    const confirmButtonColor = currentIsActive ? "#dc2626" : "#16a34a";
    const confirmText = currentIsActive ? "Yes, Deactivate" : "Yes, Activate";

    if (currentIsActive) {
      Swal.fire({
        title: "Confirm Status Change",
        text: `Are you sure you want to deactivate ${emp.first_name}?`,
        icon: "warning",
        input: "text",
        inputLabel: "Deactivation Reason",
        inputPlaceholder: "Enter reason for deactivation",
        inputValidator: (value) => {
          if (!value) return "Deactivation reason is required!";
        },
        showCancelButton: true,
        confirmButtonColor: confirmButtonColor,
        cancelButtonColor: "#6b7280",
        confirmButtonText: confirmText,
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          updateEmployeeStatus(emp, false, result.value);
        }
      });
    } else {
      Swal.fire({
        title: "Confirm Status Change",
        text: `Are you sure you want to activate ${emp.first_name}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: confirmButtonColor,
        cancelButtonColor: "#6b7280",
        confirmButtonText: confirmText,
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          updateEmployeeStatus(emp, true);
        }
      });
    }
  };

  const updateEmployeeStatus = (emp, newStatus, deactivationReason = "") => {
    setTogglingId(emp.id);
    const statusData = { id: emp.id, is_active: newStatus };
    if (!newStatus && deactivationReason) {
      statusData.deactivation_reason = deactivationReason;
    }
    employeeAPI.updateEmployeeStatus(
      emp.id,
      statusData,
      (response) => {
        showSuccess(
          `Employee status updated to ${newStatus ? "Active" : "Inactive"}`
        );
        fetchEmployees();
        setTogglingId(null);
      },
      (error) => {
        showError(error?.message || "Failed to update employee status");
        setTogglingId(null);
      }
    );
  };

  const getEmployeeAuthIdentifiers = (emp) => {
    const userId = emp.user_id || emp.user?.id || emp.user || null;
    const empId = emp.employee_id || emp.id || emp.emp_id || null;
    return { userId, empId };
  };

  const updateEmployeeAuthStatus = (emp, newAuthStatus) => {
    const { empId } = getEmployeeAuthIdentifiers(emp);
    const userId = 1;

    setAuthTogglingId(emp.id);
    employeeAPI.updateEmployeeAuthStatus(
      userId,
      empId,
      newAuthStatus,
      () => {
        showSuccess(
          `Employee authorization ${newAuthStatus ? "enabled" : "disabled"}`
        );
        fetchEmployees();
        setAuthTogglingId(null);
      },
      (error) => {
        showError(error?.message || "Failed to update employee authorization");
        setAuthTogglingId(null);
      }
    );
  };

  // Key used to share the Work From Home status with the Dashboard view.
  const WFH_STORAGE_KEY = "wfh_status_map";

  const handleWorkFromHomeToggle = (emp, currentIsWfh) => {
    const actionText = currentIsWfh
      ? "turn off work from home"
      : "mark as work from home";
    const confirmText = currentIsWfh ? "Yes, Turn Off" : "Yes, Mark WFH";
    const confirmButtonColor = currentIsWfh ? "#dc2626" : "#16a34a";

    Swal.fire({
      title: "Confirm Work From Home Change",
      text: `Are you sure you want to ${actionText} for ${
        emp.user_name || emp.name || "-"
      }?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: confirmButtonColor,
      cancelButtonColor: "#6b7280",
      confirmButtonText: confirmText,
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        updateEmployeeWorkFromHome(emp, !currentIsWfh);
      }
    });
  };

  const updateEmployeeWorkFromHome = (emp, newWfhStatus) => {
    const { empId } = getEmployeeAuthIdentifiers(emp);
    const requesterId = Number(localStorage.getItem("user_id")) || 1;

    setWfhTogglingId(emp.id);
    wfhAPI.updateEmployeeWorkFromHome(
      requesterId,
      empId,
      newWfhStatus,
      () => {
        showSuccess(
          `Work from home ${newWfhStatus ? "enabled" : "disabled"} for ${
            emp.user_name || emp.name || "employee"
          }`
        );
        setEmployees((prev) =>
          prev.map((e) =>
            e.id === emp.id ? { ...e, is_wfh_enabled: newWfhStatus } : e
          )
        );
        // Persist so the Dashboard can show a Work From Home / check-in banner.
        // Keyed by emp.id since that's what matches localStorage "user_id"
        // for the logged-in employee (see the currentEmployee lookup above).
        try {
          const raw = localStorage.getItem(WFH_STORAGE_KEY);
          const map = raw ? JSON.parse(raw) : {};
          map[emp.id] = newWfhStatus;
          localStorage.setItem(WFH_STORAGE_KEY, JSON.stringify(map));
        } catch (e) {
          // ignore storage errors
        }
        setWfhTogglingId(null);
      },
      (error) => {
        showError(error?.message || "Failed to update work from home status");
        setWfhTogglingId(null);
      }
    );
  };

  const handleDeleteEmployee = (id, name) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        employeeAPI.deleteEmployee(
          id,
          (response) => {
            showSuccess(response?.message || `${name} deleted successfully`);
            setTimeout(() => fetchEmployees(), 500);
          },
          (error) => {
            showError(error?.message || "Failed to delete employee");
          }
        );
      }
    });
  };

  const columns = useMemo(() => {
    const loggedInUserId = Number(localStorage.getItem("user_id"));
    const currentEmployee = employees.find(
      (emp) => Number(emp.id) === loggedInUserId
    );
    const showEmployeeProbationColumn =
      !isAdmin && currentEmployee?.is_probationary === true;

    return [
      // S.NO
      columnHelper.display({
        id: "sno",
        header: () => (
          <p className="text-xs font-bold text-gray-600 dark:text-white">
            S.NO
          </p>
        ),
        cell: (info) => (
          <p className="text-xs font-bold text-navy-700 dark:text-white">
            {(currentPage - 1) * ITEMS_PER_PAGE + info.row.index + 1}
          </p>
        ),
      }),

      // NAME
      columnHelper.accessor("name", {
        id: "name",
        header: () => (
          <p className="text-xs font-bold text-gray-600 dark:text-white">
            NAME
          </p>
        ),
        cell: (info) => {
          const emp = info.row.original;
          const profileSrc =
            emp.profile_picture && emp.profile_picture.trim() !== ""
              ? emp.profile_picture
              : emp.gender?.trim().toLowerCase() === "female"
              ? femaleProfile
              : maleProfile;
          return (
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full bg-brand-100 dark:bg-brand-900">
                <img
                  className="h-full w-full rounded-full object-cover"
                  src={profileSrc}
                  alt="Profile"
                />
              </div>
              <div className="flex min-w-0 flex-col">
                <p className="max-w-[170px] truncate text-xs font-bold text-navy-700 dark:text-white">
                  {info.getValue() || "-"}
                </p>
                <p className="max-w-[170px] truncate text-[11px] font-medium text-gray-500 dark:text-gray-400">
                  {emp.emp_code || "-"}
                </p>
              </div>
            </div>
          );
        },
      }),

      // JOIN DATE
      columnHelper.accessor("joinDate", {
        id: "joinDate",
        header: () => (
          <p className="text-xs font-bold text-gray-600 dark:text-white">
            JOIN DATE
          </p>
        ),
        cell: (info) => (
          <p className="text-violet-700 dark:text-violet-300 whitespace-nowrap text-xs font-semibold">
            {info.getValue() || "-"}
          </p>
        ),
      }),

      // EMAIL
      columnHelper.accessor("email", {
        id: "email",
        header: () => (
          <p className="text-xs font-bold text-gray-600 dark:text-white">
            EMAIL
          </p>
        ),
        cell: (info) => (
          <p className="text-sky-700 dark:text-sky-300 max-w-[280px] break-all text-xs font-semibold xl:max-w-[360px]">
            {info.getValue() || "-"}
          </p>
        ),
      }),

      // ROLE
      columnHelper.accessor("role_name", {
        id: "role_name",
        header: () => (
          <p className="text-xs font-bold text-gray-600 dark:text-white">
            ROLE
          </p>
        ),
        cell: (info) => (
          <p className="max-w-[120px] truncate text-xs text-navy-700 dark:text-white">
            {info.getValue() || "-"}
          </p>
        ),
      }),

      // DEPARTMENT
      columnHelper.accessor("department_name", {
        id: "department_name",
        header: () => (
          <p className="text-xs font-bold text-gray-600 dark:text-white">
            DEPARTMENT
          </p>
        ),
        cell: (info) => (
          <p className="max-w-[120px] truncate text-xs text-navy-700 dark:text-white">
            {info.getValue() || "-"}
          </p>
        ),
      }),
      ...(showEmployeeProbationColumn
        ? [
            columnHelper.accessor("is_probationary", {
              id: "employee_probation_status",
              header: () => (
                <p className="text-xs font-bold text-gray-600 dark:text-white">
                  PROBATION PERIOD
                </p>
              ),
              cell: () => (
                <p className="whitespace-nowrap text-xs font-semibold text-green-600 dark:text-green-400">
                  Active
                </p>
              ),
            }),
          ]
        : []),
      ...(isAdmin
        ? [
            // PROBATIONARY
            columnHelper.accessor("is_probationary", {
              id: "is_probationary",
              header: () => (
                <div className="flex justify-center">
                  <p className="text-xs font-bold text-gray-600 dark:text-white">
                    PROBATION PERIOD
                  </p>
                </div>
              ),
              cell: (info) => {
                const emp = info.row.original;
                const isProbationary = emp.is_probationary;
                const isToggling = probationTogglingId === emp.id;
                return (
                  <div className="flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => handleProbationToggle(emp, isProbationary)}
                      disabled={isToggling}
                      className="relative inline-flex h-6 w-11 cursor-pointer rounded-full transition-all hover:opacity-80 disabled:opacity-50"
                    >
                      <div
                        className={`h-6 w-11 rounded-full transition-all duration-300 ${
                          isProbationary
                            ? "bg-green-500"
                            : "bg-gray-300 dark:bg-gray-600"
                        }`}
                      />
                      <div
                        className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-all duration-300 ${
                          isProbationary ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                );
              },
            }),

            // STATUS
            columnHelper.accessor("status", {
              id: "status",
              header: () => (
                <p className="text-xs font-bold text-gray-600 dark:text-white">
                  STATUS
                </p>
              ),
              cell: (info) => {
                const emp = info.row.original;
                const isActive = emp.is_active || info.getValue() === "Active";
                const isToggling = togglingId === emp.id;
                return (
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => handleStatusToggle(emp, isActive)}
                      disabled={isToggling}
                      className="relative inline-flex h-6 w-11 cursor-pointer rounded-full transition-all hover:opacity-80 disabled:opacity-50"
                    >
                      <div
                        className={`h-6 w-11 rounded-full transition-all duration-300 ${
                          isActive
                            ? "bg-green-500"
                            : "bg-gray-300 dark:bg-gray-600"
                        }`}
                      />
                      <div
                        className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-all duration-300 ${
                          isActive ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                );
              },
            }),

            columnHelper.accessor("is_authorized", {
              id: "is_authorized",
              header: () => (
                <p className="text-xs font-bold text-gray-600 dark:text-white">
                  AUTHORIZED
                </p>
              ),
              cell: (info) => {
                const emp = info.row.original;
                const isAuthorized = emp.is_authorized === true;
                const isToggling = authTogglingId === emp.id;

                return (
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        updateEmployeeAuthStatus(emp, !isAuthorized)
                      }
                      disabled={isToggling}
                      className="relative inline-flex h-6 w-11 cursor-pointer rounded-full transition-all hover:opacity-80 disabled:opacity-50"
                    >
                      <div
                        className={`h-6 w-11 rounded-full transition-all duration-300 ${
                          isAuthorized
                            ? "bg-green-500"
                            : "bg-gray-300 dark:bg-gray-600"
                        }`}
                      />
                      <div
                        className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-all duration-300 ${
                          isAuthorized ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                );
              },
            }),

            // WORK FROM HOME
            columnHelper.accessor("is_wfh_enabled", {
              id: "is_wfh_enabled",
              header: () => (
                <div className="flex justify-center">
                  <p className="text-xs font-bold text-gray-600 dark:text-white">
                    WORK FROM HOME
                  </p>
                </div>
              ),
              cell: (info) => {
                const emp = info.row.original;
                const isWfh = emp.is_wfh_enabled === true;
                const isToggling = wfhTogglingId === emp.id;
                return (
                  <div className="flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => handleWorkFromHomeToggle(emp, isWfh)}
                      disabled={isToggling}
                      className="relative inline-flex h-6 w-11 cursor-pointer rounded-full transition-all hover:opacity-80 disabled:opacity-50"
                    >
                      <div
                        className={`h-6 w-11 rounded-full transition-all duration-300 ${
                          isWfh
                            ? "bg-green-500"
                            : "bg-gray-300 dark:bg-gray-600"
                        }`}
                      />
                      <div
                        className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-all duration-300 ${
                          isWfh ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                );
              },
            }),
          ]
        : []),

      // ACTIONS
      columnHelper.display({
        id: "actions",
        header: () => (
          <div className="flex justify-center">
            <p className="text-xs font-bold text-gray-600 dark:text-white">
              ACTIONS
            </p>
          </div>
        ),
        cell: (info) => {
          const emp = info.row.original;
          return (
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => handleViewDocuments(emp)}
                className="rounded-lg p-2 text-blue-600 transition hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900"
                title="View Documents"
              >
                <FaEye size={18} />
              </button>
              <button
                onClick={() => handleEditEmployee(emp)}
                className="rounded-lg p-2 text-blue-600 transition hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900"
                title="Edit Employee"
              >
                <MdEdit size={18} />
              </button>
            </div>
          );
        },
      }),
    ];
  }, [
    employees,
    isAdmin,
    togglingId,
    authTogglingId,
    probationTogglingId,
    wfhTogglingId,
    currentPage,
  ]);

  // ── UPDATED: filteredEmployees now includes probation filter ──
  const filteredEmployees = useMemo(() => {
    const loggedInUserId = Number(localStorage.getItem("user_id"));
    const isSuperAdmin =
      localStorage.getItem("is_super_admin") === "true" ||
      localStorage.getItem("is_super_admin") === true;

    let filtered = employees;

    if (!isSuperAdmin) {
      filtered = employees.filter((emp) => Number(emp.id) === loggedInUserId);
    }

    // ── Probation filter ──
    if (probationFilter === "on") {
      filtered = filtered.filter((emp) => emp.is_probationary === true);
    } else if (probationFilter === "off") {
      filtered = filtered.filter((emp) => emp.is_probationary !== true);
    }

    // ── Authorized filter ──
    if (authorizedFilter === "authorized") {
      filtered = filtered.filter((emp) => emp.is_authorized === true);
    } else if (authorizedFilter === "not_authorized") {
      filtered = filtered.filter((emp) => emp.is_authorized !== true);
    }

    // ── Work From Home filter ──
    if (wfhFilter === "wfh") {
      filtered = filtered.filter((emp) => emp.is_wfh_enabled === true);
    } else if (wfhFilter === "not_wfh") {
      filtered = filtered.filter((emp) => emp.is_wfh_enabled !== true);
    }

    if (!searchTerm.trim()) return filtered;

    const searchLower = searchTerm.toLowerCase();
    return filtered.filter(
      (emp) =>
        (emp.name && emp.name.toLowerCase().includes(searchLower)) ||
        (emp.email && emp.email.toLowerCase().includes(searchLower)) ||
        (emp.emp_code && emp.emp_code.toLowerCase().includes(searchLower)) ||
        (emp.role_name && emp.role_name.toLowerCase().includes(searchLower)) ||
        (emp.department_name &&
          emp.department_name.toLowerCase().includes(searchLower)) ||
        (emp.status && emp.status.toLowerCase().startsWith(searchLower)) ||
        (emp.is_authorized === true ? "authorized" : "not authorized").includes(
          searchLower
        )
    );
  }, [
    employees,
    searchTerm,
    probationFilter,
    authorizedFilter,
    wfhFilter,
    isAdmin,
  ]);

  // ── UPDATED: reset page on search OR filter change ──
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, probationFilter, authorizedFilter, wfhFilter]);

  const totalPages = Math.ceil(filteredEmployees.length / ITEMS_PER_PAGE);

  const paginatedEmployees = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredEmployees.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredEmployees, currentPage]);

  const table = useReactTable({
    data: paginatedEmployees,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const probationaryCount = filteredEmployees.filter(
    (emp) => emp.is_probationary === true
  ).length;
  const activeCount = filteredEmployees.filter(
    (emp) => emp.is_active || emp.status === "Active"
  ).length;
  const isSelfViewOnlyMode = !isSuperAdmin;
  const selfEmployee = filteredEmployees[0] || null;
  const selfProfileSrc =
    selfEmployee?.profile_picture && selfEmployee.profile_picture.trim() !== ""
      ? selfEmployee.profile_picture
      : selfEmployee?.gender?.trim().toLowerCase() === "female"
      ? femaleProfile
      : maleProfile;
  const selfDobRaw =
    selfEmployee?.dob ||
    selfEmployee?.date_of_birth ||
    selfEmployee?.dob_date ||
    selfEmployee?.birth_date ||
    "";
  const selfDobLabel = selfDobRaw
    ? new Date(selfDobRaw).toString() !== "Invalid Date"
      ? new Date(selfDobRaw).toLocaleDateString()
      : selfDobRaw
    : "-";
  const selfMobileLabel =
    selfEmployee?.mobile_number ||
    selfEmployee?.mobile ||
    selfEmployee?.phone_number ||
    selfEmployee?.phone ||
    selfEmployee?.contact_number ||
    "-";
  const selfAuthorizedLabel =
    selfEmployee?.is_authorized === true ? "Authorized" : "Not Authorized";

  return (
    <div className="mt-3 grid h-full grid-cols-1 gap-5">
      <Card extra={"w-full h-full px-4 pb-4 sm:px-6 sm:pb-6 overflow-visible"}>
        {/* HEADER */}
        <div className="flex flex-col gap-3 pt-4 sm:gap-4">
          <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-navy-700 dark:text-white sm:text-xl">
                {isSuperAdmin ? "Employees List" : "Employee Details"}
                <span className="text-black-500 ml-2 dark:text-gray-400">
                  {isSuperAdmin ? `(${filteredEmployees.length})` : ""}
                </span>
              </h2>

              {isSuperAdmin && (
                <span className="ml-3 text-lg font-semibold text-yellow-600 dark:text-yellow-400">
                  On Probation: {probationaryCount}
                </span>
              )}
            </div>

            {isSuperAdmin && (
              <button
                onClick={() => {
                  setIsEditMode(false);
                  setEditingEmployee(null);
                  setIsModalOpen(true);
                }}
                className="linear w-full rounded-lg bg-blue-500 px-4 py-2 text-sm font-bold text-white transition duration-200 hover:bg-blue-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-blue-500 dark:active:bg-brand-600 sm:w-auto sm:text-base"
              >
                + Add Employee
              </button>
            )}
          </div>

          {/* SEARCH + PROBATION FILTER */}
          {isSuperAdmin && (
            <div className="flex flex-col items-stretch gap-3">
              {/* Search input */}
              <div className="w-full">
                <input
                  type="text"
                  placeholder="Search by name, email, employee code, role, department, status, authorized..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-lg border-2 border-gray-200 bg-white px-3 py-2 text-sm text-navy-700 placeholder-gray-400 focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-navy-700 dark:text-white dark:placeholder-gray-500"
                />
              </div>

              {/* Filters row (below search bar) */}
              <div className="flex flex-col items-stretch gap-8 sm:flex-row sm:flex-wrap sm:items-center sm:gap-8">
                <div className="mx-auto flex w-full max-w-xs overflow-hidden rounded-full border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-navy-700">
                  {[
                    { label: "All", value: "all" },
                    { label: "On Probation", value: "on" },
                    { label: "Non Probation", value: "off" },
                  ].map((opt, idx, arr) => (
                    <button
                      key={opt.value}
                      onClick={() => setProbationFilter(opt.value)}
                      className={`flex-1 px-3 py-1.5 text-xs font-bold transition-all
          ${
            probationFilter === opt.value
              ? "bg-blue-500 text-white shadow"
              : "bg-transparent text-gray-600 dark:text-gray-300"
          }
          ${idx === 0 ? "rounded-l-full" : ""}
          ${idx === arr.length - 1 ? "rounded-r-full" : ""}
          `}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                <div className="mx-auto flex w-full max-w-xs overflow-hidden rounded-full border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-navy-700">
                  {[
                    { label: "All Auth", value: "all" },
                    { label: "Authorized", value: "authorized" },
                    { label: "Not Auth", value: "not_authorized" },
                  ].map((opt, idx, arr) => (
                    <button
                      key={opt.value}
                      onClick={() => setAuthorizedFilter(opt.value)}
                      className={`flex-1 px-3 py-1.5 text-xs font-bold transition-all
          ${
            authorizedFilter === opt.value
              ? "bg-blue-500 text-white shadow"
              : "bg-transparent text-gray-600 dark:text-gray-300"
          }
          ${idx === 0 ? "rounded-l-full" : ""}
          ${idx === arr.length - 1 ? "rounded-r-full" : ""}
          `}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                <div className="mx-auto flex w-full max-w-xs overflow-hidden rounded-full border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-navy-700">
                  {[
                    { label: "All WFH", value: "all" },
                    { label: "WFH", value: "wfh" },
                    { label: "In Office", value: "not_wfh" },
                  ].map((opt, idx, arr) => (
                    <button
                      key={opt.value}
                      onClick={() => setWfhFilter(opt.value)}
                      className={`flex-1 px-3 py-1.5 text-xs font-bold transition-all
          ${
            wfhFilter === opt.value
              ? "bg-blue-500 text-white shadow"
              : "bg-transparent text-gray-600 dark:text-gray-300"
          }
          ${idx === 0 ? "rounded-l-full" : ""}
          ${idx === arr.length - 1 ? "rounded-r-full" : ""}
          `}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {/* Clear search button */}
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="rounded-lg bg-red-100 px-3 py-2 text-sm font-bold text-red-600 hover:bg-red-200 dark:bg-red-900 dark:text-red-200 dark:hover:bg-red-800"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* TABLE SECTION */}
        <div className="mt-6 sm:mt-8">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Loading employees...
              </p>
            </div>
          ) : employees.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                No employees found
              </p>
              <button
                onClick={fetchEmployees}
                className="text-sm font-semibold text-[#0EA5E9] hover:text-[#0EA5E9]"
              >
                ↻ Refresh
              </button>
            </div>
          ) : isSelfViewOnlyMode ? (
            !selfEmployee ? (
              <div className="flex flex-col items-center justify-center py-12">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  No employee details found.
                </p>
              </div>
            ) : (
              <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-navy-800 sm:p-6 lg:p-7">
                <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-16 overflow-hidden rounded-full ring-4 ring-blue-100 dark:ring-blue-400/20 sm:h-20 sm:w-20">
                      <img
                        className="h-full w-full rounded-full object-cover"
                        src={selfProfileSrc}
                        alt="Profile"
                      />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">
                        {selfEmployee.name || "-"}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-300">
                        {selfEmployee.emp_code || "-"} ·{" "}
                        {selfEmployee.department_name || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleViewDocuments(selfEmployee)}
                      className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-600 transition hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-900/30 dark:text-blue-300"
                      title="View Documents"
                    >
                      <FaEye size={16} />
                    </button>
                    <button
                      onClick={() => handleEditEmployee(selfEmployee)}
                      className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 px-3 py-2 text-sm font-semibold text-white transition hover:from-blue-600 hover:to-indigo-600"
                      title="Edit Employee"
                    >
                      <MdEdit size={16} />
                      Edit
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div
                    className="rounded-xl border border-indigo-100 bg-indigo-50/70 p-3 dark:border-indigo-400/20 dark:bg-indigo-400/10"
                    // style={{
                    //   // borderColor: "#0ea5e9",
                    //   backgroundColor: "rgba(14, 165, 233, 0.12)",
                    // }}
                  >
                    <p
                      className="text-xs font-semibold uppercase tracking-wide"
                      style={{ color: "#0284c7" }}
                    >
                      Email
                    </p>
                    <p className="mt-1 break-all text-sm font-bold">
                      {selfEmployee.email || "-"}
                    </p>
                  </div>
                  <div className="rounded-xl border border-indigo-100 bg-indigo-50/70 p-3 dark:border-indigo-400/20 dark:bg-indigo-400/10">
                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-300">
                      Role
                    </p>
                    <p className="mt-1 text-sm font-bold text-navy-700 dark:text-white">
                      {selfEmployee.role_name || "-"}
                    </p>
                  </div>
                  <div className="rounded-xl border border-cyan-100 bg-cyan-50/70 p-3 dark:border-cyan-400/20 dark:bg-cyan-400/10">
                    <p className="text-xs font-semibold uppercase tracking-wide text-cyan-600 dark:text-cyan-300">
                      Department
                    </p>
                    <p className="mt-1 text-sm font-bold text-navy-700 dark:text-white">
                      {selfEmployee.department_name || "-"}
                    </p>
                  </div>
                  <div
                    className="rounded-xl border border-indigo-100 bg-indigo-50/70 p-3 dark:border-indigo-400/20 dark:bg-indigo-400/10"
                    style={{
                      // borderColor: "#8b5cf6",
                      backgroundColor: "rgba(139, 92, 246, 0.12)",
                    }}
                  >
                    <p
                      className="text-xs font-semibold uppercase tracking-wide"
                      style={{ color: "#7c3aed" }}
                    >
                      Join Date
                    </p>
                    <p
                      className="mt-1 text-sm font-bold"
                      // style={{ color: "#6d28d9" }}
                    >
                      {selfEmployee.joinDate || "-"}
                    </p>
                  </div>
                  <div
                    className="rounded-xl border border-indigo-100 bg-indigo-50/70 p-3 dark:border-indigo-400/20 dark:bg-indigo-400/10"
                    style={{
                      // borderColor: "#22c55e",
                      backgroundColor: "rgba(34, 197, 94, 0.12)",
                    }}
                  >
                    <p
                      className="text-xs font-semibold uppercase tracking-wide"
                      style={{ color: "#16a34a" }}
                    >
                      Status
                    </p>
                    <p
                      className="mt-1 inline-flex w-fit rounded-full px-2.5 py-0.5 text-sm font-bold"
                      style={{
                        backgroundColor:
                          selfEmployee.status === "Active"
                            ? "rgba(34, 197, 94, 0.16)"
                            : "rgba(239, 68, 68, 0.16)",
                        color:
                          selfEmployee.status === "Active"
                            ? "#15803d"
                            : "#b91c1c",
                      }}
                    >
                      {selfEmployee.status || "-"}
                    </p>
                  </div>
                  <div className="rounded-xl border border-blue-100 bg-blue-50/70 p-3 dark:border-blue-400/20 dark:bg-blue-400/10">
                    <p className="text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-300">
                      Authorized
                    </p>
                    <p className="mt-1 text-sm font-bold text-navy-700 dark:text-white">
                      {selfAuthorizedLabel}
                    </p>
                  </div>
                  <div className="rounded-xl border border-amber-100 bg-amber-50/70 p-3 dark:border-amber-400/20 dark:bg-amber-400/10">
                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-300">
                      Probation
                    </p>
                    <p
                      className="mt-1 inline-flex w-fit rounded-full px-2.5 py-0.5 text-sm font-bold"
                      style={{
                        backgroundColor: selfEmployee.is_probationary
                          ? "rgba(34, 197, 94, 0.16)"
                          : "rgba(239, 68, 68, 0.16)",
                        color: selfEmployee.is_probationary
                          ? "#15803d"
                          : "#b91c1c",
                      }}
                    >
                      {selfEmployee.is_probationary ? "Active" : "Inactive"}
                    </p>
                  </div>
                  <div className="rounded-xl border border-pink-100 bg-pink-50/70 p-3 dark:border-pink-400/20 dark:bg-pink-400/10">
                    <p className="text-xs font-semibold uppercase tracking-wide text-pink-600 dark:text-pink-300">
                      DOB
                    </p>
                    <p className="mt-1 text-sm font-bold text-navy-700 dark:text-white">
                      {selfDobLabel}
                    </p>
                  </div>
                  <div className="rounded-xl border border-teal-100 bg-teal-50/70 p-3 dark:border-teal-400/20 dark:bg-teal-400/10">
                    <p className="text-xs font-semibold uppercase tracking-wide text-teal-600 dark:text-teal-300">
                      Mobile Number
                    </p>
                    <p className="mt-1 text-sm font-bold text-navy-700 dark:text-white">
                      {selfMobileLabel}
                    </p>
                  </div>
                </div>
              </div>
            )
          ) : (
            <>
              {/* ========== MOBILE CARD VIEW ========== */}
              <div className="block lg:hidden">
                {paginatedEmployees.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      No employees match the selected filter.
                    </p>
                  </div>
                ) : (
                  paginatedEmployees.map((emp, idx) => {
                    const profileSrc =
                      emp.profile_picture && emp.profile_picture.trim() !== ""
                        ? emp.profile_picture
                        : emp.gender?.trim().toLowerCase() === "female"
                        ? femaleProfile
                        : maleProfile;
                    const isSelf =
                      Number(localStorage.getItem("user_id")) === emp.id;
                    const serialNumber =
                      (currentPage - 1) * ITEMS_PER_PAGE + idx + 1;

                    return (
                      <div
                        key={emp.id || idx}
                        className="mb-4 flex flex-col gap-3 rounded-2xl border border-gray-100 bg-white px-4 py-4 shadow-md dark:bg-navy-800/90 dark:text-white"
                      >
                        {/* Card Header */}
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-blue-100 bg-blue-50 text-sm font-bold text-blue-700">
                            {serialNumber}
                          </div>
                          <div className="h-10 w-10 overflow-hidden rounded-full bg-brand-100 dark:bg-brand-900">
                            <img
                              className="h-full w-full rounded-full object-cover"
                              src={profileSrc}
                              alt="Profile"
                            />
                          </div>
                          <div>
                            <div className="font-extrabold text-navy-900 dark:text-white">
                              {emp.name || "-"}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-300">
                              {emp.department_name || "-"}
                            </div>
                          </div>
                        </div>

                        <hr className="border-gray-200 dark:border-gray-600" />

                        {/* Card Details */}
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-300">
                              Role
                            </p>
                            <p className="font-bold text-navy-900 dark:text-white">
                              {emp.role_name || "-"}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-300">
                              Join Date
                            </p>
                            <p className="text-violet-700 dark:text-violet-300 font-bold">
                              {emp.joinDate || "-"}
                            </p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-300">
                              Email
                            </p>
                            <p className="text-sky-700 dark:text-sky-300 break-all font-bold">
                              {emp.email || "-"}
                            </p>
                          </div>

                          {/* Status */}
                          <div>
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-300">
                              Status
                            </p>
                            <div className="mt-1 flex items-center gap-2">
                              {isAdmin && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleStatusToggle(
                                      emp,
                                      emp.status === "Active"
                                    )
                                  }
                                  className="relative inline-flex h-6 w-11 rounded-full transition-all"
                                >
                                  <div
                                    className={`h-6 w-11 rounded-full ${
                                      emp.status === "Active"
                                        ? "bg-green-500"
                                        : "bg-gray-300"
                                    }`}
                                  />
                                  <div
                                    className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-all duration-300 ${
                                      emp.status === "Active"
                                        ? "translate-x-6"
                                        : "translate-x-1"
                                    }`}
                                  />
                                </button>
                              )}
                              <span
                                className={`rounded-full px-2 py-0.5 text-sm font-bold ${
                                  emp.status === "Active"
                                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                                    : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                                }`}
                              >
                                {emp.status}
                              </span>
                            </div>
                          </div>

                          {/* Probationary */}
                          <div>
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-300">
                              Probationary
                            </p>
                            <div className="mt-1 flex items-center gap-2">
                              {isAdmin && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleProbationToggle(
                                      emp,
                                      emp.is_probationary
                                    )
                                  }
                                  className="relative inline-flex h-6 w-11 rounded-full transition-all"
                                >
                                  <div
                                    className={`h-6 w-11 rounded-full ${
                                      emp.is_probationary
                                        ? "bg-green-500"
                                        : "bg-gray-300"
                                    }`}
                                  />
                                  <div
                                    className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-all duration-300 ${
                                      emp.is_probationary
                                        ? "translate-x-6"
                                        : "translate-x-1"
                                    }`}
                                  />
                                </button>
                              )}
                              <span
                                className={`text-sm font-bold ${
                                  emp.is_probationary
                                    ? "text-green-600"
                                    : "text-red-600"
                                }`}
                              >
                                {emp.is_probationary ? "Active" : "Inactive"}
                              </span>
                            </div>
                          </div>

                          <div>
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-300">
                              Authorized
                            </p>
                            <div className="mt-1 flex items-center gap-2">
                              {isAdmin && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateEmployeeAuthStatus(
                                      emp,
                                      !(emp.is_authorized === true)
                                    )
                                  }
                                  disabled={authTogglingId === emp.id}
                                  className="relative inline-flex h-6 w-11 rounded-full transition-all disabled:opacity-50"
                                >
                                  <div
                                    className={`h-6 w-11 rounded-full ${
                                      emp.is_authorized === true
                                        ? "bg-green-500"
                                        : "bg-gray-300"
                                    }`}
                                  />
                                  <div
                                    className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-all duration-300 ${
                                      emp.is_authorized === true
                                        ? "translate-x-6"
                                        : "translate-x-1"
                                    }`}
                                  />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        <hr className="border-gray-200 dark:border-gray-600" />

                        {/* Card Actions */}
                        <div className="flex items-center justify-center gap-6">
                          <button
                            onClick={() => handleViewDocuments(emp)}
                            className="text-blue-600 dark:text-blue-400"
                            title="View Documents"
                          >
                            <FaEye size={20} />
                          </button>
                          {(isAdmin || isSelf) && (
                            <button
                              onClick={() => handleEditEmployee(emp)}
                              className="text-blue-600 dark:text-blue-400"
                              title="Edit Employee"
                            >
                              <MdEdit size={20} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* ========== DESKTOP TABLE VIEW ========== */}
              <div className="hidden lg:block">
                {paginatedEmployees.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      No employees match the selected filter.
                    </p>
                  </div>
                ) : (
                  <table className="w-full table-auto border-collapse">
                    <thead>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <tr
                          key={headerGroup.id}
                          className="border-b border-gray-200 dark:border-gray-700"
                        >
                          {headerGroup.headers.map((header) => (
                            <th
                              key={header.id}
                              className="px-3 py-3 text-left align-middle"
                              onClick={header.column.getToggleSortingHandler()}
                              style={{ cursor: "pointer" }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.map((row) => (
                        <tr
                          key={row.id}
                          className="border-b border-gray-200 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              className="px-3 py-4 text-left align-middle"
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>

        {/* PAGINATION */}
        {isSuperAdmin && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={ITEMS_PER_PAGE}
            totalItems={filteredEmployees.length}
            onPageChange={setCurrentPage}
            className="mt-6 pb-4 sm:mt-8"
          />
        )}
      </Card>

      {/* ADD / EDIT MODAL */}
      <AddEmployeeModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setIsEditMode(false);
          setEditingEmployee(null);
        }}
        onSubmit={handleEmployeeSubmit}
        isEditMode={isEditMode}
        editingEmployee={editingEmployee}
      />

      {/* DOCUMENT MODAL */}
      {documentModalOpen && selectedEmployeeForDocs && (
        <div className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center p-3 backdrop-blur-sm sm:p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-2xl dark:bg-navy-800 sm:rounded-2xl">
            {/* Modal Header */}
            <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-navy-800 sm:px-6 sm:py-4">
              <div>
                <h2 className="text-lg font-bold text-navy-700 dark:text-white sm:text-2xl">
                  Documents
                </h2>
                <p className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                  {selectedEmployeeForDocs.name}
                </p>
              </div>
              <button
                onClick={() => setDocumentModalOpen(false)}
                className="text-2xl font-bold text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ✕
              </button>
            </div>

            {/* Documents List */}
            <div className="space-y-3 p-4 sm:p-6">
              {[
                {
                  key: "sslcCertificate",
                  label: "SSLC Certificate",
                  bgColor: "bg-purple-100 dark:bg-purple-900",
                  iconColor: "text-purple-600 dark:text-purple-300",
                },
                {
                  key: "relieving_letter",
                  label: "Relieving Letter",
                  bgColor: "bg-orange-100 dark:bg-orange-900",
                  iconColor: "text-orange-600 dark:text-orange-300",
                },
                {
                  key: "bank_passbook",
                  label: "Bank Passbook",
                  bgColor: "bg-indigo-100 dark:bg-indigo-900",
                  iconColor: "text-indigo-600 dark:text-indigo-300",
                },
                {
                  key: "salary_slips",
                  label: "Payslips",
                  bgColor: "bg-yellow-100 dark:bg-yellow-900",
                  iconColor: "text-yellow-600 dark:text-yellow-300",
                },
                {
                  key: "aadhaar_card",
                  label: "Aadhaar",
                  bgColor: "bg-pink-100 dark:bg-pink-900",
                  iconColor: "text-pink-600 dark:text-pink-300",
                },
                {
                  key: "pan_card",
                  label: "PAN Card",
                  bgColor: "bg-blue-100 dark:bg-blue-900",
                  iconColor: "text-blue-600 dark:text-blue-300",
                },
                {
                  key: "offer_letter",
                  label: "Offer Letter",
                  bgColor: "bg-teal-100 dark:bg-teal-900",
                  iconColor: "text-teal-600 dark:text-teal-300",
                },
              ].map((doc) => {
                const fileUrl = selectedEmployeeForDocs[doc.key];
                const isUploaded = !!fileUrl;
                const fullUrl = fileUrl
                  ? fileUrl.startsWith("http")
                    ? fileUrl
                    : fileUrl
                  : "";
                const fileName = fileUrl ? fileUrl.split("/").pop() : null;

                return (
                  <div
                    key={doc.key}
                    className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-navy-700 sm:flex-row sm:items-center sm:justify-between sm:p-4"
                  >
                    <div className="flex flex-1 items-center gap-3 sm:gap-4">
                      <div className={`${doc.bgColor} rounded-lg p-2 sm:p-3`}>
                        <div className={`${doc.iconColor} text-xl sm:text-2xl`}>
                          <IoDocumentText />
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-navy-700 dark:text-white sm:text-base">
                          {doc.label}
                        </p>
                        {isUploaded ? (
                          <div>
                            <p className="text-xs font-medium text-green-600 dark:text-green-400">
                              Uploaded
                            </p>
                            <p className="mt-0.5 break-all text-[10px] text-gray-500 dark:text-gray-400">
                              {fileName}
                            </p>
                          </div>
                        ) : (
                          <p className="text-xs font-medium text-red-500 dark:text-red-400">
                            Not uploaded
                          </p>
                        )}
                      </div>
                    </div>
                    {isUploaded && (
                      <a
                        href={fullUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full whitespace-nowrap rounded-lg bg-blue-500 px-3 py-1.5 text-center text-xs font-medium text-white hover:bg-blue-600 sm:w-auto sm:px-4 sm:py-2 sm:text-sm"
                      >
                        View/Download
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employee;
